import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import { useMsal } from '@azure/msal-react';
import jobsApi from '../services/jobsApi';

/**
 * App-wide background job tracking.
 *
 * Long-running operations (postcode bulk lookups, Karma exports, and
 * anything added later) start a job on the backend and get back a jobId.
 * Instead of each screen running its own setInterval + progress bar that
 * dies the moment the user navigates away, this context polls every active
 * job from one place, mounted once at the top of the app, and a persistent
 * toast tray (see JobsToastTray.jsx) renders whatever's in here regardless
 * of which page is currently showing.
 *
 * Most jobs use the generic backend endpoints (routes/jobs.js) for status
 * and download. A job can instead pass its own fetchStatus/download
 * functions if it already has feature-specific endpoints (e.g. Karma
 * export's existing /Karma/contacts/exportReadable/status + /download) —
 * either way the polling, retry, and toast UI are shared.
 *
 * Active job ids are mirrored to localStorage, keyed by the signed-in MSAL
 * account, so a job survives a page refresh — on load we resume polling
 * anything that was still running for that account.
 */

const POLL_INTERVAL_MS = 2000;
const POLL_REQUEST_TIMEOUT_MS = 15000; // generous vs. the 2s tick, but bounds a hung request
const FINISHED_JOB_RETENTION_MS = 5 * 60 * 1000; // keep completed/failed toasts visible for 5 min
const MAX_TRANSIENT_FAILURES = 5;
const BASE_STORAGE_KEY = 'happyEngine.activeJobIds';

const JobsContext = createContext(null);

const isTransientError = (error) => {
  const status = error?.response?.status;
  const code = error?.code;
  return status === 502 || status === 503 || status === 504 || code === 'ERR_NETWORK' || code === 'ERR_POLL_TIMEOUT';
};

// Bounds a single poll request so a stalled one (backend hang, dropped
// connection) surfaces as a retryable transient failure for that job alone,
// rather than leaving it (and, before this, every other job sharing one
// in-flight flag) stuck waiting forever.
const withTimeout = (promise, ms) => {
  let timeoutId;
  const timeout = new Promise((_, reject) => {
    timeoutId = setTimeout(() => {
      const error = new Error('Poll request timed out');
      error.code = 'ERR_POLL_TIMEOUT';
      reject(error);
    }, ms);
  });
  return Promise.race([promise, timeout]).finally(() => clearTimeout(timeoutId));
};

// One browser profile can hold more than one signed-in MSAL account (or a
// different person signs in after a logout), so the plain "active job ids"
// key is scoped per account — otherwise the next account to open the app
// would resume polling, and could be offered the download for, a job it
// never started.
const storageKeyFor = (accountId) => (accountId ? `${BASE_STORAGE_KEY}.${accountId}` : BASE_STORAGE_KEY);

// Only plain, serializable fields are persisted — a job's onComplete/
// fetchStatus/download are closures and can't survive a refresh. Jobs
// registered with a custom fetchStatus/download aren't persisted at all
// (see `persist` below): resuming them against the generic endpoint would
// silently poll/download the wrong thing. A resumed generic job still
// polls the generic endpoint and shows progress/completion in the tray, it
// just won't run feature-specific logic (merging results back into a page)
// if the page that started it isn't open anymore.
const readStoredJobs = (storageKey) => {
  try {
    const stored = localStorage.getItem(storageKey);
    const parsed = stored ? JSON.parse(stored) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
};

const writeStoredJobs = (jobs, storageKey) => {
  try {
    const persistable = jobs.map((job) => ({ jobId: job.jobId, label: job.label, downloadable: job.downloadable }));
    localStorage.setItem(storageKey, JSON.stringify(persistable));
  } catch {
    // Best-effort only — losing this just means a job won't resume after a refresh.
  }
};

export const JobsProvider = ({ children }) => {
  const { instance } = useMsal();
  const accountId = instance.getActiveAccount()?.homeAccountId || null;
  // Kept in a ref (not state) so `persist` always writes to whichever
  // account is active right now without needing to be re-created (and
  // re-subscribed to by every caller) on every account change.
  const storageKeyRef = useRef(storageKeyFor(accountId));
  const [jobs, setJobs] = useState(() => {
    const stored = readStoredJobs(storageKeyRef.current);
    return stored.map((job) => ({
      jobId: job.jobId,
      label: job.label || 'Resuming…',
      downloadable: job.downloadable,
      status: 'processing',
      phase: 'processing',
      percent: 0,
      message: 'Resuming after page reload…',
      error: null
    }));
  });
  const jobsRef = useRef(jobs);
  jobsRef.current = jobs;
  // Per-job callbacks/overrides live outside React state (not serializable,
  // and we don't want them re-registered on every render) keyed by jobId.
  const onCompleteRef = useRef(new Map());
  const fetchStatusRef = useRef(new Map());
  const transientFailuresRef = useRef(new Map());

  const persist = useCallback((list) => {
    // A job with a custom fetchStatus/download (e.g. Karma export, which
    // uses its own feature-specific endpoints) can't actually resume after
    // a refresh — those are closures registered by the page that started
    // the job, and a resumed job would silently poll/download from the
    // wrong (generic) endpoints instead. Exclude those from persistence
    // rather than restoring something that won't work.
    const resumable = list.filter((job) =>
      (job.status === 'queued' || job.status === 'processing') && !fetchStatusRef.current.has(job.jobId)
    );
    writeStoredJobs(resumable, storageKeyRef.current);
  }, []);

  // If the active MSAL account changes (a different user signs in without a
  // full page reload), stop tracking/polling whatever the previous account
  // had in flight and switch to that account's own stored jobs, if any —
  // never keep offering another account's job ids or downloads.
  useEffect(() => {
    const nextKey = storageKeyFor(accountId);
    if (nextKey === storageKeyRef.current) return;
    storageKeyRef.current = nextKey;
    onCompleteRef.current.clear();
    fetchStatusRef.current.clear();
    transientFailuresRef.current.clear();
    const stored = readStoredJobs(nextKey);
    setJobs(stored.map((job) => ({
      jobId: job.jobId,
      label: job.label || 'Resuming…',
      downloadable: job.downloadable,
      status: 'processing',
      phase: 'processing',
      percent: 0,
      message: 'Resuming after page reload…',
      error: null
    })));
  }, [accountId]);

  const updateJob = useCallback((jobId, patch) => {
    setJobs((prev) => {
      const next = prev.map((job) => (job.jobId === jobId ? { ...job, ...patch } : job));
      persist(next);
      return next;
    });
  }, [persist]);

  /**
   * Register a job that's already been started on the backend (caller made
   * the "start" call itself, since start payloads differ per feature) and
   * begin tracking/polling it here.
   * @param {String} jobId
   * @param {Object} options
   * @param {String} options.label shown in the toast
   * @param {Boolean} [options.downloadable] show a Download button once complete
   * @param {Function} [options.onComplete] called once with the finished job (status, result, error)
   * @param {Function} [options.fetchStatus] async () => axios response, defaults to GET /api/jobs/:id/status
   * @param {Function} [options.download] async () => axios response (blob), defaults to GET /api/jobs/:id/download
   */
  const trackJob = useCallback((jobId, { label, downloadable = false, onComplete, fetchStatus, download } = {}) => {
    if (onComplete) onCompleteRef.current.set(jobId, onComplete);
    if (fetchStatus || download) fetchStatusRef.current.set(jobId, { fetchStatus, download });
    setJobs((prev) => {
      const next = [...prev, {
        jobId, label, downloadable,
        status: 'queued', phase: 'queued', percent: 0, message: '', error: null
      }];
      persist(next);
      return next;
    });
  }, [persist]);

  const dismissJob = useCallback((jobId) => {
    onCompleteRef.current.delete(jobId);
    fetchStatusRef.current.delete(jobId);
    transientFailuresRef.current.delete(jobId);
    setJobs((prev) => {
      const next = prev.filter((job) => job.jobId !== jobId);
      persist(next);
      return next;
    });
  }, [persist]);

  /**
   * How a completed job's file (if any) should be downloaded — used by
   * JobsToastTray. Returns (onDownloadProgress) => Promise<axios response>.
   */
  const getDownloader = useCallback((jobId) => {
    const custom = fetchStatusRef.current.get(jobId)?.download;
    if (custom) return custom;
    return (onDownloadProgress) => jobsApi.downloadJob(jobId, instance, onDownloadProgress);
  }, [instance]);

  // Tracked per job, not as one flag for the whole round — a poll request
  // that never settles (backend hang, dropped connection) must not freeze
  // every other active job's polling along with it. POLL_REQUEST_TIMEOUT_MS
  // additionally bounds each individual request so a stalled one surfaces
  // as a (retryable) transient failure instead of hanging forever.
  const inFlightJobIdsRef = useRef(new Set());

  useEffect(() => {
    const pollOne = (job) => {
      const customFetchStatus = fetchStatusRef.current.get(job.jobId)?.fetchStatus;
      const requestPromise = customFetchStatus ? customFetchStatus() : jobsApi.getJobStatus(job.jobId, instance);

      // `withTimeout` only races the promise — it can't actually cancel the
      // underlying axios request (a custom fetchStatus has no way to accept
      // a signal either), so the real request stays outstanding after a
      // timeout fires. Keep the job marked in-flight until requestPromise
      // itself finally settles, not just until the timeout race does —
      // otherwise a hung backend would pile up a fresh real request on top
      // of the still-pending one every POLL_INTERVAL_MS despite the
      // in-flight guard.
      requestPromise.catch(() => {}).finally(() => {
        inFlightJobIdsRef.current.delete(job.jobId);
      });

      return withTimeout(requestPromise, POLL_REQUEST_TIMEOUT_MS).then((response) => {
        const data = response?.data || {};
        transientFailuresRef.current.delete(job.jobId);
        const justFinished = (data.status === 'completed' || data.status === 'failed') && job.status !== data.status;

        updateJob(job.jobId, {
          ...data,
          finishedAt: (data.status === 'completed' || data.status === 'failed') ? Date.now() : null
        });

        if (justFinished) {
          const onComplete = onCompleteRef.current.get(job.jobId);
          if (onComplete) {
            onComplete({ status: data.status, result: data.result, error: data.error });
            onCompleteRef.current.delete(job.jobId);
          }
        }
      }, (error) => {
        const failures = (transientFailuresRef.current.get(job.jobId) || 0) + 1;
        if (isTransientError(error) && failures < MAX_TRANSIENT_FAILURES) {
          transientFailuresRef.current.set(job.jobId, failures);
          updateJob(job.jobId, { message: `Temporary connection issue. Retrying (${failures}/${MAX_TRANSIENT_FAILURES})…` });
          return;
        }
        const failureMessage = error?.response?.data?.message || error.message || 'Lost track of this job.';
        updateJob(job.jobId, {
          status: 'failed',
          error: failureMessage,
          finishedAt: Date.now()
        });
        // A job failing this way (permanent polling error, or the transient
        // retry budget above running out) is just as much a completion as
        // the backend reporting status: 'failed' in the success branch above
        // — the caller's onComplete is what clears page-level busy/guard
        // state (e.g. QueryPage's postcodeLookupBusy), so skipping it here
        // left that state stuck forever on a poll failure.
        const onComplete = onCompleteRef.current.get(job.jobId);
        if (onComplete) {
          onComplete({ status: 'failed', result: null, error: failureMessage });
          onCompleteRef.current.delete(job.jobId);
        }
      });
    };

    const poll = () => {
      const current = jobsRef.current;
      const pending = current.filter((job) =>
        (job.status === 'queued' || job.status === 'processing') && !inFlightJobIdsRef.current.has(job.jobId)
      );
      pending.forEach((job) => {
        inFlightJobIdsRef.current.add(job.jobId);
        pollOne(job);
      });
    };

    const interval = setInterval(poll, POLL_INTERVAL_MS);
    poll();
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, updateJob]);

  // Sweep finished jobs out of the tray after a while so it doesn't grow forever.
  useEffect(() => {
    const sweep = setInterval(() => {
      setJobs((prev) => {
        const now = Date.now();
        const removed = [];
        const next = prev.filter((job) => {
          const keep = !job.finishedAt || now - job.finishedAt < FINISHED_JOB_RETENTION_MS;
          if (!keep) removed.push(job.jobId);
          return keep;
        });
        if (removed.length === 0) return prev;
        // The status/download closures for a swept job otherwise live on
        // forever in these maps — every completed job (e.g. repeated Karma
        // exports) would leak until the tab is closed.
        removed.forEach((jobId) => {
          onCompleteRef.current.delete(jobId);
          fetchStatusRef.current.delete(jobId);
          transientFailuresRef.current.delete(jobId);
        });
        return next;
      });
    }, 30000);
    return () => clearInterval(sweep);
  }, []);

  const value = { jobs, trackJob, dismissJob, getDownloader };

  return (
    <JobsContext.Provider value={value}>
      {children}
    </JobsContext.Provider>
  );
};

export const useJobs = () => {
  const ctx = useContext(JobsContext);
  if (!ctx) {
    throw new Error('useJobs must be used within a JobsProvider');
  }
  return ctx;
};
